import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { PinInput } from "@/components/landing/pin-input";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  CheckCircle2,
  Gamepad2,
  MonitorSmartphone,
  ShieldCheck,
  UserRound,
  Zap,
} from "lucide-react";

const REASSURANCES = [
  {
    icon: MonitorSmartphone,
    text: "Works on any device — phone, tablet or laptop.",
  },
  {
    icon: Zap,
    text: "Jump straight into the lobby with just a nickname.",
  },
  {
    icon: ShieldCheck,
    text: "Your answers are only seen by your teacher.",
  },
];

/**
 * The "Joining an exam?" card on the landing page. This is the student entry
 * point: a six-digit PIN routes them to /join/$pin, where they pick a nickname
 * and enter the lobby. Kept in its own module so the marketing page stays a
 * plain composition of sections.
 */
export function JoinExamCard() {
  const navigate = useNavigate();
  const [pin, setPin] = useState("");
  const ready = /^\d{6}$/.test(pin);

  function handleSubmit(e) {
    e.preventDefault();
    if (ready) navigate({ to: "/join/$pin", params: { pin } });
  }

  return (
    <Card
      className="glass group relative mx-auto max-w-4xl overflow-hidden rounded-3xl border-2 p-0 shadow-elegant transition-shadow duration-300 hover:shadow-[0_20px_60px_-18px_oklch(0.55_0.22_255/0.45)]"
      data-reveal
    >
      <div aria-hidden="true" className="absolute inset-x-0 top-0 h-1 bg-gradient-primary" />
      <div className="grid lg:grid-cols-2">
        {/* Explainer */}
        <div className="flex flex-col justify-center p-5 sm:p-10 lg:p-12">
          <Badge variant="secondary" className="mb-5 w-fit gap-1.5 rounded-full px-3 py-1">
            <UserRound className="size-3.5" aria-hidden="true" /> For students
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight text-balance">Joining an exam?</h2>
          <p className="mt-3 text-muted">
            Enter the six-digit PIN your teacher shared and you&rsquo;re in. No account, no
            downloads.
          </p>

          <ul className="mt-6 space-y-3">
            {REASSURANCES.map((item) => (
              <li key={item.text} className="flex items-start gap-3 text-sm">
                <span className="mt-0.5 grid size-6 shrink-0 place-items-center rounded-full bg-accent/10 text-accent">
                  <item.icon className="size-3.5" aria-hidden="true" />
                </span>
                <span className="text-muted">{item.text}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* PIN entry */}
        <div className="relative border-t border-border/60 bg-gradient-card p-5 sm:p-10 lg:border-l lg:border-t-0">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-16 -top-16 size-48 rounded-full bg-accent/10 blur-3xl"
          />
          <form onSubmit={handleSubmit} className="relative space-y-6">
            <div className="space-y-3">
              <p className="text-center text-sm font-medium text-muted">Enter your game PIN</p>
              <PinInput value={pin} onChange={setPin} showProgress />
              <p
                aria-live="polite"
                className={cn(
                  "flex items-center justify-center gap-1.5 text-xs font-medium",
                  ready ? "text-accent" : "text-muted",
                )}
              >
                {ready ? (
                  <>
                    <CheckCircle2 className="size-3.5" aria-hidden="true" /> PIN looks good — press
                    Join game
                  </>
                ) : (
                  "Your teacher will read out the PIN."
                )}
              </p>
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={!ready}
              className="h-12 w-full bg-gradient-primary text-base shadow-elegant transition-transform hover:-translate-y-0.5 active:translate-y-0"
            >
              {ready ? (
                <CheckCircle2 className="size-5" aria-hidden="true" />
              ) : (
                <Gamepad2 className="size-5" aria-hidden="true" />
              )}
              Join game
            </Button>

            <p className="text-center text-xs text-muted">
              Ask your teacher if you don&rsquo;t have a PIN yet.
            </p>
          </form>
        </div>
      </div>
    </Card>
  );
}
